<template>
  <div>
    <base-header
      v-if="!embedded"
      class="pb-9 pt-4"
      :type="$route.meta.headerClass ? $route.meta.headerClass : 'success'"
    >
    </base-header>
    <modal :show.sync="showBbcModal" size="sm" modal-classes="modal">
      <div class="p-3">
        <p class="mb-3">Does this stream include BBC commentary?</p>
        <div class="d-flex justify-content-between">
          <base-button type="success" @click="confirmActivateWithBbc(true)">Yes</base-button>
          <base-button type="secondary" @click="confirmActivateWithBbc(false)">No</base-button>
        </div>
      </div>
    </modal>
    <div :class="embedded ? 'container-fluid mt-0' : 'container-fluid mt--9'">
      <div class="row">
        <div class="col">
          <div
            v-if="embedded"
            class="d-flex align-items-center flex-wrap mb-3"
          >
            <span class="h3 mb-0 mr-3">{{ streamTitle }}</span>
            <router-link
              v-if="streamDocId"
              class="btn btn-sm btn-outline-secondary"
              :to="{ name: 'AWS Stream Config', params: { id: streamDocId } }"
            >
              Full AWS stream page
            </router-link>
          </div>
          <div
            v-if="embedded && !hasLinkedStream && matchId && user.roles.livestream_admin"
            class="mb-3"
          >
            <base-button
              type="success"
              size="sm"
              :disabled="creatingMatchStream"
              @click="createAwsStreamForMatch"
            >
              Create livestream for this match
            </base-button>
            <span class="text-muted small ml-2">
              Uses the venue’s MediaConnect flow from
              <router-link :to="{ path: '/livestreams/venue-ingest' }">Venue ingest</router-link>
              (same ingest IP for every match at that ground).
            </span>
          </div>
          <div v-else class="h1 ml-3 mb-4 text-white">
            {{ streamTitle }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header" v-if="hasStreamData">
              <span class="h3">Encoder</span>
            </div>
            <div class="card-body" v-if="hasStreamData">
              <form>
                <div class="alert alert-secondary small mb-3">
                  AWS live · Signal:
                  <strong>{{ signalHealthLabel }}</strong>
                  <span v-if="pendingShutdownText"> · {{ pendingShutdownText }}</span>
                </div>
                <div class="row mb-5">
                  <div class="col">
                    <base-button
                      v-if="
                        stream.sep_state === 'off' ||
                        stream.sep_state === 'waiting' ||
                        stream.sep_state === 'pending_deactivation'
                      "
                      type="success"
                      block
                      @click="promptActivate"
                      >Activate</base-button
                    >
                    <base-button
                      v-if="
                        stream.sep_state === 'activation_in_progress' ||
                        stream.sep_state === 'on' ||
                        stream.sep_state === 'ready'
                      "
                      type="warning"
                      block
                      @click="deactivateAwsStream"
                      >Deactivate</base-button
                    >
                  </div>
                  <div class="col m-xy-auto">State: {{ stream.sep_state }}</div>
                  <div
                    @click="refresh"
                    class="col-auto m-xy-auto psuedobutton text-primary"
                  >
                    <i class="fas fa-sync"></i>
                  </div>
                </div>
                <div class="text-muted small mb-3">
                  Preview uses Brightcove player when a video id exists; AWS-only streams may have no preview.
                </div>
                <div
                  v-if="iframe"
                  style="position: relative; display: block; max-width: 100%"
                >
                  <div style="padding-top: 56.25%">
                    <iframe
                      :src="iframe"
                      allowfullscreen=""
                      allow="encrypted-media"
                      style="
                        position: absolute;
                        top: 0px;
                        right: 0px;
                        bottom: 0px;
                        left: 0px;
                        width: 100%;
                        height: 100%;
                      "
                    ></iframe>
                  </div>
                </div>

                <base-input
                  label="Stream Protocol"
                  v-model="stream.protocol"
                  disabled
                />

                <base-input
                  label="Stream Address"
                  :value="streamAddress"
                  id="aws-live-address"
                  @focus="copyText('aws-live-address')"
                />
              </form>
            </div>
            <div class="card-body" v-else-if="!hasLinkedStream">
              <div class="text-muted small">
                Stream details appear after an admin creates a livestream for this match (button above),
                using the venue’s MediaConnect flow.
              </div>
            </div>
            <div class="card-body" v-else>
              <loading-spinner></loading-spinner>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card shadow">
            <div class="card-header" v-if="hasStreamData">
              <span class="h3">Sends</span>
            </div>
            <div class="card-body" v-if="hasStreamData">
              <div class="mb-4">
                <div class="font-weight-bold mb-2">{{ takersSectionTitle }}</div>
                <p v-if="!takerEditRows.length" class="text-muted small mb-3">
                  <template v-if="embedded && matchId">
                    No takers on this feed yet. Add below. The fixture list is the default; stream-only
                    rows here override it for edge cases.
                  </template>
                  <template v-else>No takers on this feed yet. Add destinations below.</template>
                </p>
                <div
                  v-if="user.roles.livestream_admin"
                  class="mb-3 pb-3 border-bottom"
                >
                  <div class="small font-weight-bold mb-2">Add taker</div>
                  <div class="d-flex flex-wrap align-items-end">
                    <div class="flex-grow-1 mr-2" style="min-width: 200px">
                      <el-select
                        v-model="takerIdToAdd"
                        filterable
                        clearable
                        placeholder="Choose taker…"
                        class="w-100"
                      >
                        <el-option
                          v-for="t in takersAvailableToAdd"
                          :key="t.id"
                          :label="t.name || t.id"
                          :value="t.id"
                        />
                      </el-select>
                    </div>
                    <base-button
                      type="secondary"
                      size="sm"
                      :disabled="!takerIdToAdd"
                      @click="addTakerRow"
                    >
                      Add
                    </base-button>
                  </div>
                </div>
                <div
                  v-for="(row, idx) in takerEditRows"
                  :key="row.takerId + '-' + idx"
                  class="d-flex align-items-center mb-2"
                >
                  <div class="flex-grow-1 small">{{ takerLabel(row.takerId) }}</div>
                  <base-switch v-model="row.takeBbcComms" class="mr-2" />
                  <span class="small text-muted mr-2">BBC comms</span>
                </div>
                <base-button
                  v-if="user.roles.livestream_admin && takerEditRows.length"
                  type="primary"
                  size="sm"
                  @click="saveTakerRoutes"
                  >Save taker routes</base-button
                >
              </div>
              <div
                v-if="outputRtmpSends.length === 0"
                class="text-muted small"
              >
                No RTMP outputs.
              </div>
              <ul
                v-else
                class="list-group list-group-flush list mb-3"
              >
                <li
                  class="list-group-item px-0 pb-2 border-0"
                >
                  <div class="row small text-muted font-weight-bold">
                    <div class="col-md-7">RTMP URL</div>
                    <div class="col-md-5">Video / audio</div>
                  </div>
                </li>
                <li
                  v-for="(row, idx) in outputRtmpSends"
                  class="list-group-item px-0"
                  :key="(row.url || '') + '-' + idx"
                >
                  <rtmp :output="row"></rtmp>
                </li>
              </ul>
            </div>
            <div class="card-body" v-else-if="!hasLinkedStream">
              <div class="text-muted small">
                No stream yet. Set fixture takers on the match, create the livestream, then activate.
              </div>
            </div>
            <div class="card-body" v-else>
              <loading-spinner></loading-spinner>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RTMP from './Components/RTMP'
import { Firebase } from '@/firebase.js'

export default {
  name: 'MatchLiveAwsPanel',
  components: { rtmp: RTMP },
  props: {
    /** Firestore livestreams document id (may be empty before first go-live) */
    streamDocId: { type: String, default: '' },
    /** When set (e.g. from Match page), sent on activate so match livestreamTakerRoutes merge even if the stream doc lacks matchId */
    matchId: { type: String, default: '' },
    /** Label for new livestream rows and header before a stream doc exists */
    matchTitle: { type: String, default: '' },
    /** When true, omit page header and tighten layout (e.g. Match page Live tab) */
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      showBbcModal: false,
      takerEditRows: [],
      takerIdToAdd: '',
      creatingMatchStream: false,
    }
  },
  computed: {
    user() {
      return this.$store.getters['user/user']
    },
    stream() {
      return this.$store.getters['livestreams/stream']
    },
    hasStreamData() {
      if (!this.stream) return false
      return this.stream.provider === 'aws' && Boolean(this.stream.id)
    },
    hasLinkedStream() {
      return Boolean(this.streamDocId)
    },
    signalHealthLabel() {
      const a = this.stream && this.stream.automation
      if (!a || !a.signalHealth) return 'unknown'
      return a.signalHealth
    },
    pendingShutdownText() {
      const a = this.stream && this.stream.automation
      if (!a || !a.pendingAutoShutdownAt) return ''
      try {
        const d = a.pendingAutoShutdownAt.toDate
          ? a.pendingAutoShutdownAt.toDate()
          : new Date(a.pendingAutoShutdownAt)
        return `Auto-shutdown pending: ${d.toISOString()}`
      } catch (e) {
        return ''
      }
    },
    streamTitle() {
      if (
        this.stream &&
        this.stream.videocloud &&
        this.stream.videocloud.video &&
        this.stream.videocloud.video.name
      ) {
        return this.stream.videocloud.video.name
      }
      if (this.embedded && this.matchTitle) {
        return this.matchTitle
      }
      return 'AWS live stream'
    },
    token() {
      return this.$store.getters['livestreams/token']
    },
    outputRtmpSends() {
      const o = this.stream && this.stream.outputs
      if (!o || !Array.isArray(o.rtmp)) return []
      return o.rtmp.filter((row) => row && (row.url || row.connection_info))
    },
    iframe() {
      const videoID = this.stream && this.stream.job_videocloud_asset_id
      if (!videoID) return ''
      let url =
        'https://players.brightcove.net/4854911276001/default_default/index.html?videoId=' +
        videoID
      if (this.token) {
        url += '&adConfigId=' + this.token
      }
      return url
    },
    streamAddress() {
      return (this.stream && this.stream.stream && this.stream.stream.url) || ''
    },
    takersSectionTitle() {
      return this.embedded && this.matchId
        ? 'Takers (fixture first; stream overrides optional)'
        : 'Takers (this stream)'
    },
    takersAvailableToAdd() {
      const list = this.$store.getters['takers/array'] || []
      const taken = new Set((this.takerEditRows || []).map((r) => r.takerId))
      return list.filter((t) => t && t.id && t.enabled !== false && !taken.has(t.id))
    },
  },
  watch: {
    stream: {
      deep: true,
      handler(s) {
        if (!s || !Array.isArray(s.taker_routes_effective)) {
          this.takerEditRows = []
          return
        }
        this.takerEditRows = JSON.parse(JSON.stringify(s.taker_routes_effective))
      },
    },
    streamDocId: {
      immediate: true,
      handler() {
        this.loadStream()
      },
    },
    matchId() {
      this.loadStream()
    },
  },
  mounted() {
    this.$store.dispatch('takers/fetchAndAdd')
  },
  methods: {
    createAwsStreamForMatch() {
      if (!this.matchId || !this.user.roles.livestream_admin) return
      const name =
        (this.matchTitle && String(this.matchTitle).trim()) ||
        (this.stream && this.stream.videocloud && this.stream.videocloud.video && this.stream.videocloud.video.name) ||
        'Live stream'
      this.creatingMatchStream = true
      const publish = Firebase.app().functions('europe-west2').httpsCallable('addStream')
      publish({
        name,
        provider: 'aws',
        matchId: this.matchId,
      })
        .then((res) => {
          const id = res.data && res.data.id
          if (!id) throw new Error('No stream id returned')
          return id
        })
        .then((id) => {
          this.$emit('livestream-created', id)
          this.$notify({
            message: 'Livestream template created',
            type: 'success',
            timeout: 4000,
          })
          this.$nextTick(() => this.loadStream())
        })
        .catch((e) => {
          console.error(e)
          this.$notify({
            message: e.message || 'Create livestream failed',
            type: 'danger',
            timeout: 8000,
          })
        })
        .finally(() => {
          this.creatingMatchStream = false
        })
    },
    loadStream() {
      if (!this.streamDocId) return
      const payload = { id: this.streamDocId }
      if (this.matchId) payload.matchId = this.matchId
      this.$store.dispatch('livestreams/getStream', payload)
    },
    refresh() {
      this.loadStream()
    },
    takerLabel(takerId) {
      const t = this.$store.getters['takers/byId'](takerId)
      return (t && t.name) || takerId
    },
    catalogDefaultTakeBbcComms(takerId) {
      const t = this.$store.getters['takers/byId'](takerId)
      if (!t) return true
      if (t.bbcAudioAllowed !== undefined) return t.bbcAudioAllowed !== false
      if (t.defaultTakeBbcComms !== undefined) return t.defaultTakeBbcComms !== false
      return true
    },
    addTakerRow() {
      const tid = this.takerIdToAdd
      if (!tid || this.takerEditRows.some((r) => r.takerId === tid)) return
      this.takerEditRows.push({
        takerId: tid,
        takeBbcComms: this.catalogDefaultTakeBbcComms(tid),
        enabled: true,
      })
      this.takerIdToAdd = ''
    },
    buildStreamOverridesFromEditedEffective() {
      const matchTpl = (this.stream && this.stream.taker_routes_match_template) || []
      const newOvr = []
      for (const row of this.takerEditRows || []) {
        if (!row || !row.takerId) continue
        const m = matchTpl.find((x) => x && x.takerId === row.takerId)
        const baseTake = m
          ? m.takeBbcComms !== undefined
            ? !!m.takeBbcComms
            : this.catalogDefaultTakeBbcComms(row.takerId)
          : this.catalogDefaultTakeBbcComms(row.takerId)
        const baseEnabled = m ? m.enabled !== false : true
        const rowTake = !!row.takeBbcComms
        const rowEnabled = row.enabled !== false
        if (!m) {
          newOvr.push({
            takerId: row.takerId,
            takeBbcComms: rowTake,
            enabled: rowEnabled,
          })
        } else if (rowTake !== baseTake || rowEnabled !== baseEnabled) {
          newOvr.push({
            takerId: row.takerId,
            takeBbcComms: rowTake,
            enabled: rowEnabled,
          })
        }
      }
      return newOvr
    },
    promptActivate() {
      this.showBbcModal = true
    },
    confirmActivateWithBbc(val) {
      this.showBbcModal = false
      this.runActivate({ bbcCommsDeclared: val })
    },
    runActivate({ bbcCommsDeclared }) {
      const payload = { streamID: this.stream.id, bbcCommsDeclared }
      if (this.matchId) {
        payload.matchId = this.matchId
      }
      this.$notify({
        message: 'Please wait for activation',
        timeout: 5000,
        type: 'default',
        overlap: true,
        verticalAlign: 'bottom',
        horizontalAlign: 'right',
      })
      this.$store
        .dispatch('livestreams/activateStream', payload)
        .then(() => {
          this.$store.dispatch('livestreams/getStream', {
            id: this.streamDocId,
          })
          this.$notify({
            message: 'Activated',
            timeout: 3000,
            type: 'success',
            overlap: true,
            verticalAlign: 'bottom',
            horizontalAlign: 'right',
          })
        })
        .catch((error) => {
          console.error('[activateStream] callable failed', error)
          this.$notify({
            message: `Activate failed: ${
              error && error.message ? error.message : 'Unknown error'
            }`,
            timeout: 5000,
            type: 'danger',
            overlap: true,
            verticalAlign: 'bottom',
            horizontalAlign: 'right',
          })
        })
    },
    saveTakerRoutes() {
      if (!this.user.roles.livestream_admin) return
      if (this.embedded && this.matchId) {
        this.saveTakerRoutesEmbeddedMatch()
        return
      }
      this.saveTakerRoutesStreamOverridesOnly()
    },
    saveTakerRoutesEmbeddedMatch() {
      const newMatch = this.takerEditRows.map((r) => ({
        takerId: r.takerId,
        takeBbcComms: !!r.takeBbcComms,
        enabled: r.enabled !== false,
      }))
      const writes = [
        this.$store.dispatch('matches/patch', {
          id: this.matchId,
          livestreamTakerRoutes: newMatch,
        }),
      ]
      if (this.streamDocId) {
        const streamOverrides = (this.stream && this.stream.taker_routes_stream_overrides) || []
        const streamOnly = streamOverrides.filter(
          (r) => r && r.takerId && !newMatch.some((m) => m.takerId === r.takerId)
        )
        const publish = Firebase.app()
          .functions('europe-west2')
          .httpsCallable('updateLivestreamTakerRoutes')
        writes.push(publish({ streamId: this.streamDocId, takerRoutes: streamOnly }))
      }
      Promise.all(writes)
        .then(() => {
          this.loadStream()
          this.$notify({
            message: 'Fixture taker routes saved',
            type: 'success',
            timeout: 3000,
          })
        })
        .catch((e) => {
          console.error(e)
          this.$notify({
            message: e.message || 'Save failed',
            type: 'danger',
            timeout: 5000,
          })
        })
    },
    saveTakerRoutesStreamOverridesOnly() {
      const takerRoutes = this.buildStreamOverridesFromEditedEffective()
      const publish = Firebase.app()
        .functions('europe-west2')
        .httpsCallable('updateLivestreamTakerRoutes')
      publish({
        streamId: this.streamDocId,
        takerRoutes,
      })
        .then(() => {
          this.loadStream()
          this.$notify({
            message: 'Taker routes saved',
            type: 'success',
            timeout: 3000,
          })
        })
        .catch((e) => {
          console.error(e)
          this.$notify({
            message: e.message || 'Save failed',
            type: 'danger',
            timeout: 5000,
          })
        })
    },
    copyText(id) {
      var copier = document.getElementById(id)
      var copyBit = copier.querySelector('input')
      copyBit.select()
      copyBit.setSelectionRange(0, 99999)
      document.execCommand('copy')
      this.$notify({
        message: 'Address Copied',
        timeout: 2000,
        type: 'default',
        overlap: true,
        verticalAlign: 'bottom',
        horizontalAlign: 'right',
      })
    },
    deactivateAwsStream() {
      const payload = { streamID: this.stream.id }
      this.$notify({
        message: 'Please wait for deactivation',
        timeout: 5000,
        type: 'default',
        overlap: true,
        verticalAlign: 'bottom',
        horizontalAlign: 'right',
      })
      this.$store
        .dispatch('livestreams/deactivateStream', payload)
        .then(() => {
          this.$store.dispatch('livestreams/getStream', {
            id: this.streamDocId,
          })
          this.$notify({
            message: 'Deactivated',
            timeout: 3000,
            type: 'success',
            overlap: true,
            verticalAlign: 'bottom',
            horizontalAlign: 'right',
          })
        })
        .catch((error) => {
          console.error('[deactivateStream] callable failed', error)
          this.$notify({
            message: `Deactivate failed: ${
              error && error.message ? error.message : 'Unknown error'
            }`,
            timeout: 5000,
            type: 'danger',
            overlap: true,
            verticalAlign: 'bottom',
            horizontalAlign: 'right',
          })
        })
    },
  },
}
</script>

<style>
.psuedobutton :hover {
  cursor: pointer;
}
</style>
